import { isAxiosError } from 'axios';

import assetLibraryClient from '@/api/assetLibraryClient';

import {
  rowOutcomes,
  submitBrief,
  updateBrief,
  type BriefLineOut,
  type BriefOut,
  type BriefPatch,
} from '../../briefs/api';
import type { LinkedDraft } from './useBasket';

/**
 * Checkout: submit the basket as a Brief, or save it as a draft.
 *
 * "Buying" an asset means briefing it for localisation. One request carries the
 * whole basket rather than one per row, because the order is the thing the user
 * placed: a per-row loop would half-succeed in a way that leaves some rows
 * briefed and some not, with no reference tying them together and no honest
 * message to show for it.
 *
 * Backed by `POST /briefs` (`{ submit: true }`). A brief line is validated per
 * campaign row: `submit_brief` on the backend rejects or promotes an entire
 * row's lines together, so a row is always either fully ordered or fully
 * skipped -- never split.
 */

export interface ProductionOrderRow {
  /** Campaign row id, as returned by GET /campaigns/briefing-rows. */
  campaign_id: string;
  /** Library asset ids picked for that row. */
  asset_ids: string[];
}

export interface ProductionOrderRequest {
  rows: ProductionOrderRow[];
  /** ISO date (YYYY-MM-DD). Null means no date given. */
  needed_by: string | null;
  /** Free text, applied to every row in the order. */
  brief: string;
}

export interface ProductionOrderResponse {
  /**
   * Human-quotable brief reference, e.g. `BR-2026-0417`. Null when every row
   * was skipped -- no reference is allocated unless something is submittable.
   */
  reference: string | null;
  /** Rows whose lines were all submitted (brief line status `ordered`). */
  ordered: string[];
  /**
   * Rows whose lines were all rejected (e.g. an asset's rights expired between
   * being added and submit) -- reason comes from the backend's per-line
   * result_reason.
   */
  skipped: { campaign_id: string; reason: string }[];
  /**
   * The brief's `updated_at` after the call. Only sending a reopened draft
   * fills it in: the basket stays linked to a draft nothing was ordered from,
   * and needs to know it last wrote this version.
   */
  updatedAt?: string;
}

function summarizeBriefLines(lines: BriefLineOut[]): Omit<ProductionOrderResponse, 'reference'> {
  const ordered: string[] = [];
  const skipped: { campaign_id: string; reason: string }[] = [];
  // Anything not ordered is reported as skipped: just after a send, a row that
  // did not go through is work nobody is doing, whatever its line status.
  for (const [campaignId, outcome] of rowOutcomes(lines)) {
    if (outcome.status === 'ordered') {
      ordered.push(campaignId);
    } else {
      skipped.push({
        campaign_id: campaignId,
        reason: outcome.reason ?? 'Asset no longer eligible for this brief.',
      });
    }
  }
  return { ordered, skipped };
}

function createBrief(body: ProductionOrderRequest, submit: boolean) {
  const lines = body.rows.flatMap((row) =>
    row.asset_ids.map((asset_id) => ({ campaign_id: row.campaign_id, asset_id })),
  );
  return assetLibraryClient.post<BriefOut>('/briefs', {
    needed_by: body.needed_by,
    notes: body.brief,
    lines,
    submit,
  });
}

export async function sendToProduction(
  body: ProductionOrderRequest,
): Promise<ProductionOrderResponse> {
  const response = await createBrief(body, true);
  return { ...summarizeBriefLines(response.data.lines), reference: response.data.reference };
}

/**
 * Persist the basket as a draft brief, to be reopened from My Briefs.
 *
 * Nothing is validated beyond what creating a brief already checks (assets
 * still published and in rights, one channel) -- the per-line verdicts are
 * only written at submit, because a draft can sit for a week and a verdict
 * taken today would be stale by then.
 */
export async function saveDraft(body: ProductionOrderRequest): Promise<void> {
  await createBrief(body, false);
}

/** The whole of a draft, as the basket now holds it: replaces its lines. */
function asPatch(body: ProductionOrderRequest): BriefPatch {
  return {
    lines: body.rows.flatMap((row) =>
      row.asset_ids.map((asset_id) => ({ campaign_id: row.campaign_id, asset_id })),
    ),
    needed_by: body.needed_by,
    notes: body.brief || null,
  };
}

/** Save the basket into the draft it was opened from, instead of a new one. */
export async function saveIntoDraft(
  draft: LinkedDraft,
  body: ProductionOrderRequest,
): Promise<void> {
  await updateBrief(draft.id, withVersion(draft, asPatch(body)));
}

/**
 * Only write over the version the basket last saw: the draft may have been
 * changed since (in My Briefs, or another tab), and the basket's line set
 * would quietly undo that. Older stored links carry no version: unchecked.
 */
function withVersion(draft: LinkedDraft, patch: BriefPatch): BriefPatch {
  return draft.updatedAt ? { ...patch, expected_updated_at: draft.updatedAt } : patch;
}

/**
 * Send the draft the basket was opened from: saved first, so what goes out is
 * what the basket shows, then submitted under the draft's own id.
 */
export async function sendDraft(
  draft: LinkedDraft,
  body: ProductionOrderRequest,
  /** Called with the saved version before the submit, which may still fail. */
  onSaved?: (updatedAt: string) => void,
): Promise<ProductionOrderResponse> {
  const saved = await updateBrief(draft.id, withVersion(draft, asPatch(body)));
  onSaved?.(saved.updated_at);
  const { brief } = await submitBrief(draft.id);
  return {
    ...summarizeBriefLines(brief.lines),
    reference: brief.reference,
    updatedAt: brief.updated_at,
  };
}

/**
 * The linked draft is no longer there to write to: deleted (404), or already
 * sent (409, only drafts can be edited) -- from My Briefs, or another tab.
 */
export function isDraftGone(error: unknown): boolean {
  return isAxiosError(error) && (error.response?.status === 404 || error.response?.status === 409);
}

/** The linked draft was changed since the basket last saw it (412). */
export function isDraftChanged(error: unknown): boolean {
  return isAxiosError(error) && error.response?.status === 412;
}

interface InvalidAssetDetail {
  message: string;
  invalid: { asset_id: string; reason: string }[];
}

function isInvalidAssetDetail(detail: unknown): detail is InvalidAssetDetail {
  return (
    typeof detail === 'object' &&
    detail !== null &&
    Array.isArray((detail as InvalidAssetDetail).invalid)
  );
}

/**
 * What to tell the user when creating a brief fails.
 *
 * An asset that expired or was deleted after it went into the basket is
 * refused up front with a structured 422 naming each one. Collapsing that to
 * a generic "could not send" leaves the user to guess which of a dozen assets
 * to remove, so the offenders are named, by filename where we still have it.
 */
export function describeBriefError(
  error: unknown,
  filenameFor: (assetId: string) => string | undefined,
  fallback: string,
  /** Where the user removes them from: the basket, or a draft being edited. */
  container = 'the basket',
): string {
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (isInvalidAssetDetail(detail) && detail.invalid.length > 0) {
    const named = detail.invalid.map(
      ({ asset_id, reason }) => `${filenameFor(asset_id) ?? asset_id}: ${reason}`,
    );
    return `Remove these from ${container} first — ${named.join('; ')}`;
  }
  return fallback;
}
